import { type ComponentType } from 'react';
import { Wizard } from 'react-use-wizard';
import { useSelector } from 'react-redux';

import { selectFeatureFlags } from '@client/model/selectors';
import { GuidedPlanningProvider } from './context';
import { GuidedPlanningHeader } from './components/guided-planning-header';
import { WizardAnimatedStep } from './components/animated-step';
import {
  FundingPlanOverviewStep,
  AssetSelectionStep,
  AssessmentIntroStep,
  AssessmentAccountsStep,
  AssessmentProjectionStep,
  PreferencesDeathBenefitsStep,
  PreferencesBenefitStructureStep,
  QuotesLoadingStep,
  QuotesSelectionStep,
  CcrcTypeASelectionStep,
  CcrcTypeCFeasibilityStep,
  QuotesFundingStep,
  PlanComparisonStep,
} from './components/steps';
import { getFlatStepConfigs, getGuidedPlanningVariant, type GuidedPlanningVariant } from './config';
import { useWizardStepSync } from './hooks';

interface GuidedFinancialPlanningPageProps {
  clientId: string;
}

const STEP_COMPONENTS: Record<string, ComponentType> = {
  'overview': FundingPlanOverviewStep,
  'assets': AssetSelectionStep,
  'assessment-intro': AssessmentIntroStep,
  'assessment-accounts': AssessmentAccountsStep,
  'assessment-projection': AssessmentProjectionStep,
  'preferences-death-benefits': PreferencesDeathBenefitsStep,
  'preferences-benefit-structure': PreferencesBenefitStructureStep,
  'quotes-loading': QuotesLoadingStep,
  'quotes-selection': QuotesSelectionStep,
  'quotes-funding': QuotesFundingStep,
  'plan-comparison': PlanComparisonStep,
};

const STEP_COMPONENT_OVERRIDES: Partial<
  Record<GuidedPlanningVariant, Record<string, ComponentType>>
> = {
  'ccrc-type-a': { 'quotes-selection': CcrcTypeASelectionStep },
};

function resolveStepComponent(
  variant: GuidedPlanningVariant,
  financialFeasibility: boolean,
  stepId: string,
): ComponentType {
  if (financialFeasibility && stepId === 'assessment-projection') {
    return CcrcTypeCFeasibilityStep;
  }

  const component = STEP_COMPONENT_OVERRIDES[variant]?.[stepId] ?? STEP_COMPONENTS[stepId];

  if (!component) {
    throw new Error(`No guided planning step component for "${stepId}"`);
  }

  return component;
}

function GuidedPlanningWizard() {
  const { startIndex, onStepChange } = useWizardStepSync();
  const featureFlags = useSelector(selectFeatureFlags);
  const variant = getGuidedPlanningVariant(featureFlags);
  const financialFeasibility = !!featureFlags.financialFeasibility;
  const stepConfigs = getFlatStepConfigs(variant, financialFeasibility);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Wizard
        startIndex={startIndex}
        header={<GuidedPlanningHeader />}
        onStepChange={onStepChange}
        wrapper={<WizardAnimatedStep />}
      >
        {stepConfigs.map(config => {
          const StepComponent = resolveStepComponent(variant, financialFeasibility, config.id);
          return <StepComponent key={config.id} />;
        })}
      </Wizard>
    </div>
  );
}

export function GuidedFinancialPlanningPage({ clientId }: GuidedFinancialPlanningPageProps) {
  return (
    <GuidedPlanningProvider clientId={clientId}>
      <GuidedPlanningWizard />
    </GuidedPlanningProvider>
  );
}
